import Image from 'components/Common/Image'
import NestableClickable from 'components/Common/NestableClickable'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import TextButton from 'LuxKit/Button/TextButton'
import SolidPlusCircleIcon from 'LuxKit/Icons/solid/SolidPlusCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import EmptyMustDoImage from 'tripPlanner/components/EntryPoints/Common/EmptyMustDoImage'
import GooglePlaceRating from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRating'
import type { RegenerateIndividualItemActivity } from 'tripPlanner/hooks/api/regenerateIndividualItem'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { TripItem } from 'tripPlanner/types/tripItem'
import RegenerateItemDetails from './RegenerateItemDetails'

const IconContainer = styled.div`
  grid-area: icon;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AddIcon = styled(SolidPlusCircleIcon)`
  width: ${rem(24)};
  height: ${rem(24)};
  color: ${props => props.theme.palette.neutral.default.two};
`

const Container = styled(NestableClickable)`
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: inset 0 0 0 ${rem(1)} ${props => props.theme.palette.neutral.default.six};
  border-radius: ${rem(12)};
  padding: ${rem(12)};
  display: grid;
  gap: ${rem(12)};
  grid-template: "image content icon" auto / ${rem(42)} 1fr auto;
  transition: box-shadow 200ms;

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} ${rem(20)};
    gap: ${rem(20)};
  grid-template: "image content icon" auto / ${rem(48)} 1fr auto;
  }

  ${mediaHoverable} {
    &:hover {
      box-shadow: inset 0 0 0 ${rem(2)} ${props => props.theme.palette.neutral.default.two};
    }
  }

  &.disabled ${IconContainer} {
    opacity: 0.5;
  }
  
  &:focus {
    ${interactiveFocusStyle}
  }
`

const ImageContainer = styled.div`
  grid-area: image;
  display: flex;
  overflow: hidden;
  align-self: stretch;
  border-radius: ${rem(2)};
  width: ${rem(42)};
  height: ${rem(42)};
`

const ContentContainer = styled(Group)`
  grid-area: content;
  flex: 1;
`

const AllowHyphenationBodyText = styled(BodyText)`
  hyphens: auto;
`

const ViewDetailsButton = styled(TextButton)`
  width: ${rem(66)};
`

interface RegenerateItemTileProps {
  itemToReplace: TripItem
  activity: RegenerateIndividualItemActivity
  disabled?: boolean
  onClick: (activity: RegenerateIndividualItemActivity) => void
}

export default function RegenerateItemTile({
  itemToReplace,
  activity,
  disabled = false,
  onClick,
}: RegenerateItemTileProps) {
  const intl = useIntl()
  const openModal = useOpenModal()
  const isDesktop = useScreenSizeUp('desktop')

  const viewDetailsLabel = intl.formatMessage({
    id: 'gWVQDgtgiz',
    defaultMessage: 'View details',
    description: 'Button to view full details of a regenerated itinerary item suggestion',
  })

  const handleClick = useCallback(() => {
    if (!disabled) {
      onClick(activity)
    }
  }, [activity, disabled, onClick])

  const handleViewDetails = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    openModal(RegenerateItemDetails, { itemToReplace, activity })
  }, [activity, itemToReplace, openModal])

  const placeId = activity.googlePlaceDetails?.placeId

  return (
    <Container
      onClick={handleClick}
      disabled={disabled}
    >
      <ImageContainer>
        {activity.googlePlaceDetails.photoUrl && (
          <Image
            alt={activity.basicActivity.name}
            image={{
              url: activity.googlePlaceDetails.photoUrl,
              title: activity.basicActivity.name,
            }}
            fit="center"
            loading="lazy"
          />
        )}
        {!activity.googlePlaceDetails.photoUrl && (
          <EmptyMustDoImage />
        )}
      </ImageContainer>

      <ContentContainer direction="vertical" gap={8} tabletGap={12}>
        <Group direction="vertical" gap={4} verticalAlign="center">
          <Group
            direction="horizontal"
            horizontalAlign="space-between"
            verticalAlign="start"
            gap={4}
          >
            <Heading variant="heading6" align="start" lineClamp={2} wrap="word-wrap">
              {activity.basicActivity.name}
            </Heading>
            {isDesktop && <ViewDetailsButton size="small" kind="tertiary" onClick={handleViewDetails}>
              {viewDetailsLabel}
            </ViewDetailsButton>}
          </Group>

          {placeId && (
            <GooglePlaceRating placeId={placeId} size="S" />
          )}
        </Group>

        <Group direction="vertical" gap={2} verticalAlign="center">
          {activity.basicActivity.description && (
            <AllowHyphenationBodyText
              variant="small"
              align="start"
              lineClamp={2}
              colour="neutral-two"
              wrap="word-wrap"
            >
              {activity.basicActivity.description}
            </AllowHyphenationBodyText>

          )}

          {!isDesktop && (
            <ViewDetailsButton
              size="small"
              kind="tertiary"
              onClick={handleViewDetails}
            >
              {viewDetailsLabel}
            </ViewDetailsButton>
          )}
        </Group>
      </ContentContainer>

      <IconContainer>
        <AddIcon />
      </IconContainer>
    </Container>
  )
}
